import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';
import type { AutocompleteProvider, HandlebarsBlockType } from '../../types';
import { HandlebarsBlockView } from './HandlebarsBlockView';

export interface HandlebarsBlockOptions {
  HTMLAttributes: Record<string, unknown>;
  autocompleteProvider?: AutocompleteProvider;
  showDescriptions?: boolean;
  allowFreeformHandlebars?: boolean;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    handlebarsBlock: {
      insertHandlebarsBlock: (attrs: {
        blockType: HandlebarsBlockType;
        variable: string;
        blockId: string;
        description?: string | null;
        variableDescription?: string | null;
        isValid?: boolean;
        error?: string | null;
        nonEditable?: boolean;
      }) => ReturnType;
      updateHandlebarsBlock: (
        blockId: string,
        attrs: Partial<{
          blockType: HandlebarsBlockType;
          variable: string;
          description: string | null;
          variableDescription: string | null;
          isValid: boolean;
          error: string | null;
        }>
      ) => ReturnType;
    };
  }
}

export const HandlebarsBlock = Node.create<HandlebarsBlockOptions>({
  name: 'handlebarsBlock',

  group: 'block',
  content: 'block+',
  defining: true,
  isolating: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      autocompleteProvider: undefined,
      showDescriptions: false,
      allowFreeformHandlebars: false,
    };
  },

  addAttributes() {
    return {
      blockType: {
        default: 'if',
        parseHTML: (element) =>
          (element.getAttribute('data-block-type') as HandlebarsBlockType) || 'if',
        renderHTML: (attributes) => ({
          'data-block-type': attributes.blockType,
        }),
      },
      variable: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-variable') || '',
        renderHTML: (attributes) => ({
          'data-variable': attributes.variable,
        }),
      },
      blockId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-block-id') || '',
        renderHTML: (attributes) => ({
          'data-block-id': attributes.blockId,
        }),
      },
      description: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-description') || null,
        renderHTML: (attributes) => {
          if (!attributes.description) return {};
          return { 'data-description': attributes.description };
        },
      },
      variableDescription: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-variable-description') || null,
        renderHTML: (attributes) => {
          if (!attributes.variableDescription) return {};
          return { 'data-variable-description': attributes.variableDescription };
        },
      },
      isValid: {
        default: true,
        parseHTML: (element) => element.getAttribute('data-valid') !== 'false',
        renderHTML: (attributes) => ({
          'data-valid': attributes.isValid ? 'true' : 'false',
        }),
      },
      error: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-error') || null,
        renderHTML: (attributes) => {
          if (!attributes.error) return {};
          return { 'data-error': attributes.error };
        },
      },
      nonEditable: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-non-editable') === 'true',
        renderHTML: (attributes) => {
          if (!attributes.nonEditable) return {};
          return { 'data-non-editable': 'true' };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-handlebars-block]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-handlebars-block': '' },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HandlebarsBlockView, {
      contentDOMElementTag: 'div',
    });
  },

  addCommands() {
    return {
      insertHandlebarsBlock:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              blockType: attrs.blockType,
              variable: attrs.variable,
              blockId: attrs.blockId,
              description: attrs.description ?? null,
              variableDescription: attrs.variableDescription ?? null,
              isValid: attrs.isValid ?? true,
              error: attrs.error ?? null,
              nonEditable: attrs.nonEditable ?? false,
            },
            content: [
              {
                type: 'paragraph',
              },
            ],
          });
        },
      updateHandlebarsBlock:
        (blockId, attrs) =>
        ({ tr, state }) => {
          let updated = false;
          state.doc.descendants((node, pos) => {
            if (
              node.type.name === this.name &&
              node.attrs.blockId === blockId
            ) {
              const newAttrs = { ...node.attrs, ...attrs };
              tr.setNodeMarkup(pos, undefined, newAttrs);
              updated = true;
              return false;
            }
          });
          return updated;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Exit block on Enter at end of empty trailing paragraph
      Enter: () => {
        const { state } = this.editor;
        const { $from } = state.selection;

        // Walk up to find if we're inside a handlebarsBlock
        for (let d = $from.depth; d > 0; d--) {
          const node = $from.node(d);
          if (node.type.name === this.name) {
            // Check if cursor is in the last child and it's an empty paragraph
            const indexInBlock = $from.index(d);
            const isLastChild = indexInBlock === node.childCount - 1;
            const paragraph = $from.parent;
            const isEmptyParagraph =
              paragraph.type.name === 'paragraph' && paragraph.content.size === 0;

            // Only exit if there's more than one child (don't exit from a single empty paragraph)
            if (isLastChild && isEmptyParagraph && node.childCount > 1) {
              // Delete the empty paragraph and create a new paragraph after the block
              const { tr } = state;
              const paragraphStart = $from.before($from.depth);
              const paragraphEnd = $from.after($from.depth);
              tr.delete(paragraphStart, paragraphEnd);

              // Insert a new paragraph after the block
              const blockEnd = $from.end(d);
              // After deleting the paragraph, the position shifts
              const adjustedBlockEnd = blockEnd - (paragraphEnd - paragraphStart);
              const afterBlock = adjustedBlockEnd + 1;

              tr.insert(afterBlock, state.schema.nodes.paragraph.create());
              tr.setSelection(TextSelection.create(tr.doc, afterBlock + 1));
              this.editor.view.dispatch(tr);
              return true;
            }
            break;
          }
        }
        return false;
      },
    };
  },
});

export default HandlebarsBlock;
